import React, { Component } from 'react';
import Formatters from '../../utils/Formatters';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import className from 'classnames';

export default class UtilizationHorseshoe extends Component {
  static defaultProps = {
    availableAmount: 0,
    headingText: "You've loaned",
    infoUrl: '',
    isMobile: false,
    maxAmount: 0,
    onClickInfo: () => {},
    pendingAmount: 0,
    showDecimal: false,
    showUtilization: true,
    isExpired: false,
  };

  static propTypes = {
    /**  Amount available from the Max Amount i.e. Credit Limit's credit_remaining */
    availableAmount: PropTypes.number.isRequired,
    /**  Class name for the component's container */
    containerClassName: PropTypes.string,
    /** Copy for Heading*/
    headingText: PropTypes.string,
    /** Url the info icon links to */
    infoUrl: PropTypes.string.isRequired,
    /** Indicator if being viewed on mobile */
    isMobile: PropTypes.bool.isRequired,
    /**  Max Amount i.e. Credit Limit's max_potential_credit_limit */
    maxAmount: PropTypes.number.isRequired,
    /**  Handler for when info icon beside `Credit Limit` is clicked */
    onClickInfo: PropTypes.func.isRequired,
    /**  Amount pending i.e. Pending Drawdowns' summed total */
    pendingAmount: PropTypes.number.isRequired,
    /**  Indicator if amount displayed should have decimal */
    showDecimal: PropTypes.bool,
    /**  Indicator if the RCL is expired */
    isExpired: PropTypes.bool,
  };

  get isDisplayingInfoIcon() {
    return !isEmpty(this.props.infoUrl);
  }

  get availableAmount() {
    const { availableAmount, maxAmount } = this.props;
    if (availableAmount > maxAmount) {
      return 0.0;
    }

    return Math.max(0.0, availableAmount - this.pendingAmount);
  }

  get pendingAmount() {
    const { availableAmount, maxAmount, pendingAmount } = this.props;
    if (pendingAmount > maxAmount) {
      return maxAmount;
    }

    return Math.max(Math.min(availableAmount, pendingAmount), 0.0);
  }

  get maxAmount() {
    const { maxAmount } = this.props;

    return Math.max(maxAmount, 0.0);
  }

  get containerDimension() {
    let [width, height] = [334, 244];
    if (this.props.isMobile) {
      [width, height] = [259, 148];
    }

    return { width, height };
  }

  componentDidMount() {
    // force rerender to get ref values
    this.forceUpdate();
  }

  totalLength = () => {
    if (!this.progressBar) return 0;
    if (typeof this.progressBar.getTotalLength !== 'function') return 0;

    return Math.ceil(this.progressBar.getTotalLength());
  };

  usagePercentage = () => {
    const percentage = (this.availableAmount / this.maxAmount) * 100;
    if (isNaN(percentage)) {
      return 0;
    }

    return Math.trunc(percentage);
  };

  targetBalancePercentage = () => {
    const { targetBalance } = this.props;

    return Math.trunc((targetBalance / this.maxAmount) * 100);
  };

  pendingBarValue = () => {
    if (!this.props.showUtilization) return this.totalLength();
    if (this.maxAmount <= 0.0) return this.totalLength();

    const targetValuePercentage = Math.trunc(
      (this.pendingAmount / this.maxAmount) * 100
    );
    const pendingBarValue =
      this.totalLength() - (targetValuePercentage / 100) * this.totalLength();

    return this.totalLength() + this.progressBarValue() + pendingBarValue;
  };

  progressBarValue = () => {
    if (!this.props.showUtilization) return this.totalLength();
    if (this.usagePercentage() > 100) return 0;

    return (
      this.totalLength() - (this.usagePercentage() / 100) * this.totalLength()
    );
  };

  primaryBarColor = () => {
    return '#698CD1';
  };

  activePrimaryTextColor = () => {
    return this.props.isExpired ? '#ED4343' : '#003A70';
  };

  activeSecondaryTextColor = () => {
    return this.props.isExpired ? '#ED4343' : '#8082A9';
  };

  activeStrokeColor = () => {
    return this.props.isExpired ? '#FFE6E6' : '#E8E9FF';
  };

  formatAmount = (amount) => {
    const val = Formatters.amount({ amount: amount });

    if (this.props.showDecimal) {
      return val;
    } else {
      const wholeNumber = val.split('.')[0];
      return !!wholeNumber ? wholeNumber : '0';
    }
  };

  availableAmountWholeNumber = () => {
    return this.formatAmount(this.availableAmount).split('.')[0];
  };

  availableAmountDecimal = () => {
    const val = this.formatAmount(this.availableAmount).split('.')[1];

    return !!val ? val : '00';
  };

  renderHeadingText = () => {
    const { isMobile } = this.props;
    let x = isMobile ? '46.5%' : '50%';
    let y = isMobile ? '55%' : '50%';
    let className = isMobile
      ? 'byline _fontSize--xxsmall _whiteSpace_pre'
      : 'subhead _whiteSpace_pre';

    const text = (
      <text
        fill={this.activePrimaryTextColor()}
        className={className}
        data-testid='heading-copy'
        dominantBaseline='auto'
        textAnchor='middle'
        x={x}
        y={this.props.showUtilization ? y : '45%'}
      >
        {this.renderBrokenDownText()}
      </text>
    );
    return text;
  };

  renderBrokenDownText = () => {
    const { headingText, isMobile, showUtilization } = this.props;
    if (showUtilization) {
      return headingText;
    }
    const wordsPerLine = 3;
    const words = headingText.split(' ');
    const firstLine = words.slice(0, wordsPerLine);
    const secondLine = words.slice(wordsPerLine, wordsPerLine * 2);
    const firstSentence = firstLine.join(' ');
    const secondSentence = secondLine.join(' ');

    return [
      <tspan dy={isMobile ? '8' : '4'}>{firstSentence}</tspan>,
      <tspan x={isMobile ? '46.5%' : '50%'} dy={isMobile ? '12' : '22'}>
        {secondSentence}
      </tspan>,
    ];
  };

  renderMiddleAmount = () => {
    const { showDecimal } = this.props;
    const { isMobile } = this.props;
    let x = isMobile ? '46.5%' : '50%';
    let y = isMobile ? '67.5%' : '62.5%';
    let currencyClassName = className('subhead', {
      '_fontSize--xsmall': isMobile,
    });
    let amountClassName = className('headline', {
      '_fontSize--normal': isMobile,
    });
    let decimalClassName = className('subhead', {
      '_fontSize--xsmall': isMobile,
    });
    return (
      <g id='amountLoaned'>
        <text
          fill={this.activePrimaryTextColor()}
          x={x}
          y={this.props.showUtilization ? y : '70.0%'}
          dominantBaseline='auto'
          textAnchor='middle'
        >
          <tspan className={currencyClassName}>₱</tspan>
          <tspan
            data-testid='available-amount-whole'
            className={amountClassName}
            style={isMobile ? {} : { fontSize: '1.8em' }}
          >
            {' '}
            {this.availableAmountWholeNumber()}{' '}
          </tspan>
          {showDecimal && (
            <tspan className={decimalClassName} dx='-5'>
              .{this.availableAmountDecimal()}
            </tspan>
          )}
        </text>
      </g>
    );
  };

  renderUsageLeft = () => {
    if (!this.props.showUtilization) {
      return null;
    }

    const { isMobile } = this.props;
    let x = isMobile ? '46.5%' : '50%';
    let y = isMobile ? '77.5%' : '72.5%';

    let bylineClassName = className('byline _whiteSpace_pre', {
      '_fontSize--xxsmall': isMobile,
      '_fontSize--small': !isMobile,
    });
    return (
      <text
        fill='#8082A9'
        className={bylineClassName}
        x={x}
        y={y}
        dominantBaseline='auto'
        textAnchor='middle'
        data-testid='percentage-left'
      >
        <tspan> {this.usagePercentage()}% of limit left </tspan>
      </text>
    );
  };

  renderMiddleText = () => {
    return (
      <g>
        {this.renderHeadingText()}
        {this.renderMiddleAmount()}
        {this.renderUsageLeft()}
      </g>
    );
  };

  renderPendingPattern = () => {
    return (
      <pattern
        height='4'
        id='diagonalPendingPattern'
        width='4'
        patternTransform='rotate(42.94deg)'
        patternUnits='userSpaceOnUse'
      >
        <path
          d='M-1,1 l2,-2
           M0,4 l4,-4
           M3,5 l2,-2'
          style={{ stroke: '#474FD9', strokeWidth: 1.1 }}
        />
      </pattern>
    );
  };

  render() {
    const { isMobile } = this.props;

    if (isMobile) {
      return (
        <svg
          data-testid='utilization-horseshoe'
          width={this.containerDimension.width}
          height={this.containerDimension.height}
          viewBox={`0 0 ${this.containerDimension.width} ${this.containerDimension.height}`}
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          className={`${this.props.containerClassName}`}
        >
          {this.renderMiddleText()}
          <path
            d='M62.9113 120C60.0142 111.069 59.2657 101.579 60.7268 92.3033C62.1879 83.0276 65.8172 74.2291 71.3188 66.6251C76.8204 59.0211 84.0385 52.8268 92.3846 48.5475C100.731 44.2681 109.969 42.0247 119.345 42.0002C128.721 41.9757 137.971 44.1709 146.339 48.4066C154.707 52.6424 161.957 58.7988 167.499 66.374C173.04 73.9492 176.715 82.7287 178.224 91.9966C179.733 101.264 179.034 110.758 176.184 119.704'
            stroke={this.activeStrokeColor()}
            strokeWidth='15'
          />

          <path
            d='M62.9113 118C60.0142 109.298 59.2657 100.051 60.7268 91.0135C62.1879 81.9756 65.8172 73.4027 71.3188 65.9937C76.8204 58.5846 84.0385 52.5492 92.3846 48.3796C100.731 44.2099 109.969 42.024 119.345 42.0002C128.721 41.9763 137.971 44.1152 146.339 48.2423C154.707 52.3695 161.957 58.3681 167.499 65.749C173.04 73.13 176.715 81.6843 178.224 90.7146C179.733 99.7449 179.034 108.995 176.184 117.712'
            strokeWidth='10'
            strokeDasharray={this.totalLength()}
            strokeDashoffset={this.pendingBarValue()}
            stroke='url(#diagonalPendingPattern)'
          />

          <path
            ref={(ref) => (this.progressBar = ref)}
            d='M62.9113 118C60.0142 109.298 59.2657 100.051 60.7268 91.0135C62.1879 81.9756 65.8172 73.4027 71.3188 65.9937C76.8204 58.5846 84.0385 52.5492 92.3846 48.3796C100.731 44.2099 109.969 42.024 119.345 42.0002C128.721 41.9763 137.971 44.1152 146.339 48.2423C154.707 52.3695 161.957 58.3681 167.499 65.749C173.04 73.13 176.715 81.6843 178.224 90.7146C179.733 99.7449 179.034 108.995 176.184 117.712'
            stroke={this.primaryBarColor()}
            strokeWidth='11'
            strokeDasharray={this.totalLength()}
            strokeDashoffset={this.progressBarValue()}
          />

          <text
            id='Credit limit'
            fill='#8082A9'
            className='byline _fontSize--xsmall _whiteSpace_pre _fontWeight_bold'
          >
            <tspan
              x={this.isDisplayingInfoIcon ? '190.0' : '202.0'}
              y='96.9725'
            >
              Credit limit
            </tspan>
            {this.isDisplayingInfoIcon && (
              <tspan
                fill='#ffe5a1'
                x='246.0'
                y='99.5'
                onClick={this.props.onClickInfo}
                className='materialIcon --xxsmall _marginLeft--xxxsmall _margintop--xsmall'
                data-path={this.props.infoUrl}
                data-testid='horseshoe-cta'
              >
                info
              </tspan>
            )}
          </text>

          <g>
            <svg x='190' y='103.0' width='70' height='16'>
              <rect x='0' y='0' width='70' height='16' rx='8' fill='#E8E9FF' />
              <text
                data-testid='max-amount'
                fill='#8082A9'
                className='byline _fontSize--xsmall _fontWeight_bold'
                x='50%'
                y='55.0%'
                dominantBaseline='middle'
                textAnchor='middle'
              >
                {' '}
                ₱ {this.formatAmount(this.maxAmount)}
              </text>
            </svg>
          </g>

          <defs>
            <clipPath id='clip0'>
              <rect x='185.137' y='18' width='63' height='46' fill='white' />
            </clipPath>
            {this.renderPendingPattern()}
          </defs>
        </svg>
      );
    } else {
      return (
        <svg
          data-testid='utilization-horseshoe'
          width={this.containerDimension.width}
          height={this.containerDimension.height}
          viewBox={`0 0 ${this.containerDimension.width} ${this.containerDimension.height}`}
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          className={`${this.props.containerClassName}`}
        >
          {this.renderMiddleText()}

          <path
            d='M58.5534 194C53.0271 176.94 51.5994 158.811 54.3865 141.092C57.1736 123.373 64.0967 106.566 74.5913 92.0402C85.0859 77.5146 98.8549 65.682 114.776 57.5073C130.696 49.3326 148.318 45.0471 166.204 45.0004C184.09 44.9536 201.734 49.1469 217.697 57.2383C233.66 65.3296 247.49 77.09 258.06 91.5606C268.63 106.031 275.641 122.802 278.52 140.506C281.399 158.21 280.065 176.346 274.628 193.435'
            stroke={this.activeStrokeColor()}
            strokeWidth='31'
          />

          <path
            d='M58.5534 191C53.0271 174.283 51.5994 156.52 54.3865 139.157C57.1736 121.795 64.0967 105.326 74.5913 91.0931C85.0859 76.8599 98.8549 65.2656 114.776 57.2555C130.696 49.2454 148.318 45.0462 166.204 45.0004C184.09 44.9546 201.734 49.0634 217.697 56.9918C233.66 64.9203 247.49 76.4439 258.06 90.6231C268.63 104.802 275.641 121.236 278.52 138.583C281.399 155.931 280.065 173.702 274.628 190.447'
            strokeWidth='23'
            strokeDasharray={this.totalLength()}
            strokeDashoffset={this.pendingBarValue()}
            stroke='url(#diagonalPendingPattern)'
          />

          <path
            ref={(ref) => (this.progressBar = ref)}
            d='M58.5534 191C53.0271 174.283 51.5994 156.52 54.3865 139.157C57.1736 121.795 64.0967 105.326 74.5913 91.0931C85.0859 76.8599 98.8549 65.2656 114.776 57.2555C130.696 49.2454 148.318 45.0462 166.204 45.0004C184.09 44.9546 201.734 49.0634 217.697 56.9918C233.66 64.9203 247.49 76.4439 258.06 90.6231C268.63 104.802 275.641 121.236 278.52 138.583C281.399 155.931 280.065 173.702 274.628 190.447'
            stroke={this.primaryBarColor()}
            strokeWidth='24'
            strokeDasharray={this.totalLength()}
            strokeDashoffset={this.progressBarValue()}
          />

          <g>
            <text
              id='Credit limit'
              fill='#8082A9'
              className='byline _fontSize--xsmall _whiteSpace_pre _fontWeight_bold'
            >
              <tspan
                x={this.isDisplayingInfoIcon ? '226.0' : '234.0'}
                y='238.772'
              >
                Credit limit
              </tspan>
              {this.isDisplayingInfoIcon && (
                <tspan
                  fill='#ffe5a1'
                  x='277.5'
                  y='242.5'
                  onClick={this.props.onClickInfo}
                  className='materialIcon --xxsmall _marginLeft--xxxsmall _margintop--xsmall'
                  data-path={this.props.infoUrl}
                  data-testid='utilization-information-icon'
                >
                  info
                </tspan>
              )}
            </text>
            <g id='credit_limit_max'>
              <svg x='224' y='209.072' width='67' height='18'>
                <rect
                  x='0'
                  y='0'
                  width='67'
                  height='18'
                  rx='8'
                  fill={this.activeStrokeColor()}
                />
                <text
                  data-testid='max-amount'
                  fill={this.activeSecondaryTextColor()}
                  className='byline _fontSize--xsmall _fontWeight_bold'
                  x='50%'
                  y='50%'
                  dominantBaseline='middle'
                  textAnchor='middle'
                >
                  {' '}
                  ₱ {this.formatAmount(this.maxAmount)}
                </text>
              </svg>
            </g>

            <g id='credit_limit_base'>
              <rect
                x='54'
                y='209.072'
                width='28'
                height='18'
                rx='8'
                fill={this.activeStrokeColor()}
              />
              <text
                id='P 0'
                fill={this.activeSecondaryTextColor()}
                className='byline _fontSize--xsmall _fontWeight_bold'
              >
                <tspan x='61.4522' y='220.772'>
                  ₱ 0
                </tspan>
              </text>
            </g>
          </g>

          <defs>
            <clipPath id='clip0'>
              <rect x='256.137' y='4' width='65' height='48' fill='white' />
            </clipPath>
            {this.renderPendingPattern()}
          </defs>
        </svg>
      );
    }
  }
}



// WEBPACK FOOTER //
// ./src/features/shared/components/misc/UtilizationHorseshoe.js